// Persistent track store backed by localStorage.
// Tracks are saved when generated and read by Library and Explore pages.

const TRACK_STORE_KEY = "musicflow-tracks"

export interface StoredTrack {
    id: string
    title: string
    description: string
    model: string
    duration: number
    bpm: number
    instrumental: boolean
    shared: boolean
    audioUrl: string
    createdAt: number
    plays: number
    likes: number
    loading?: boolean
}

type TrackListener = () => void
const listeners: Set<TrackListener> = new Set()

function notify(): void {
    for (const l of listeners) l()
}

export function onTracksChange(listener: TrackListener): () => void {
    listeners.add(listener)
    return () => listeners.delete(listener)
}

export function getAllTracks(): StoredTrack[] {
    try {
        const raw = localStorage.getItem(TRACK_STORE_KEY)
        return raw ? JSON.parse(raw) : []
    } catch {
        return []
    }
}

export function getSharedTracks(): StoredTrack[] {
    return getAllTracks().filter(t => t.shared)
}

export function saveTrack(track: StoredTrack): void {
    const tracks = getAllTracks()
    tracks.unshift(track)
    localStorage.setItem(TRACK_STORE_KEY, JSON.stringify(tracks))
    notify()
}

export function updateTrack(id: string, partial: Partial<StoredTrack>): void {
    const tracks = getAllTracks()
    const idx = tracks.findIndex(track => track.id === id)
    if (idx >= 0) {
        tracks[idx] = {...tracks[idx], ...partial}
        localStorage.setItem(TRACK_STORE_KEY, JSON.stringify(tracks))
        notify()
    }
}

export function deleteTrack(id: string): void {
    const tracks = getAllTracks().filter(t => t.id !== id)
    localStorage.setItem(TRACK_STORE_KEY, JSON.stringify(tracks))
    notify()
}

export function incrementPlays(id: string): void {
    const tracks = getAllTracks()
    const t = tracks.find(t => t.id === id)
    if (t) {
        t.plays++
        localStorage.setItem(TRACK_STORE_KEY, JSON.stringify(tracks))
        notify()
    }
}

export function toggleLike(id: string): void {
    const tracks = getAllTracks()
    const t = tracks.find(t => t.id === id)
    if (t) {
        t.likes = t.likes > 0 ? t.likes - 1 : t.likes + 1
        localStorage.setItem(TRACK_STORE_KEY, JSON.stringify(tracks))
        notify()
    }
}

export function formatDuration(seconds: number): string {
    const m = Math.floor(seconds / 60)
    const s = Math.floor(seconds % 60)
    return `${m}:${s.toString().padStart(2, "0")}`
}
